import { PercentageOutlined } from '@ant-design/icons';
import { ECouponType } from '@common/enums/coupon.enum';
import { ButtonV2 } from '@common/general-ui/button-v2/button-v2.component';
import { FormField } from '@common/general-ui/form-field/form-field.component';
import { Form } from '@common/general-ui/form/form.component';
import { Icon } from '@common/general-ui/icon/icon.component';
import { Loading } from '@common/general-ui/loading/loading.component';
import { Summary } from '@common/general-ui/summary/summary.component';
import { StatusTagV2 } from '@common/general-ui/tag-v2/status-tag-v2.component';
import { useForm } from '@common/hooks/use-form.hook';
import { useTimer } from '@common/hooks/use-timer.hook';
import { customNotify } from '@common/utils/custom-notify.util';
import { formatDateWithDayOfWeek } from '@common/utils/format-date/format-date.util';
import { ERROR_CODES_MAP } from '@src/api/error-codes-map.constant';
import { apiHooks } from '@src/api/hooks/api-hooks';
import { IRegisterModel } from '@src/api/model/register.model';
import { EField } from '@src/shared/field.enum';
import { FIELDS } from '@src/shared/fields.constant';
import { calculatePrice, ICalcPriceData } from '@src/shared/utils/calculate-price.util';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from 'twin.macro';
import { useRegister } from '../../register.hook';
import { products as productsMerchandisingData } from '../productsMerchandisingData.json';

interface IFormProps {
    couponId: string;
}

export const OrderSummaryContainer = (): JSX.Element => {
    const { registerInfo, saveToRegister } = useRegister();
    const [couponId, setCouponId] = useState(registerInfo.couponId);

    const { mutate: orderInfoMutate, error: orderInfoDataError } = apiHooks.registration.useEstimateOrderInfo();
    const { data: orderInfoData } = apiHooks.registration.useGetEstimateOrderInfo(
        {
            plan_item_price_id: registerInfo.priceId,
            delivery_configuration_id: registerInfo.deliveryConfigId,
            coupon_id: couponId,
        },
        { enabled: !!registerInfo.priceId && !!registerInfo.deliveryConfigId },
    );

    const {
        data: products,
        isFetching: productLoading,
        error: productError,
        remove: productsRemove,
    } = apiHooks.packHouse.useGetProducts();

    const {
        data: deliveryConfig,
        isFetching: deliveryConfigLoading,
        error: deliveryConfigError,
        remove: deliveryConfigsRemove,
    } = apiHooks.packHouse.useGetDeliveryConfigurationById(registerInfo.deliveryConfigId || '', {
        enabled: !!registerInfo.deliveryConfigId,
    });

    const {
        mutateAsync: couponMutate,
        data: coupon,
        error: couponError,
        isLoading: couponLoading,
    } = apiHooks.item.useGetCouponById();

    useEffect(() => {
        orderInfoMutate({
            body: {
                plan_item_price_id: registerInfo.priceId,
                delivery_configuration_id: registerInfo.deliveryConfigId,
                coupon_id: couponId,
            },
        });
    }, [couponId, registerInfo.deliveryConfigId, registerInfo.priceId, orderInfoMutate]);

    // get selected product using product id
    const product = useMemo(
        () => products?.list.find((product) => product.id === registerInfo.productId),
        [products?.list, registerInfo.productId],
    );

    // get price
    const price = useMemo(
        () => product?.item?.prices.find((f) => f.id === registerInfo.priceId),
        [product?.item?.prices, registerInfo.priceId],
    );

    // get product merchandising data
    const productMerchandisingData = registerInfo.productId ? productsMerchandisingData[registerInfo.productId] : '';

    // construct box info object for summary card
    const boxInfo = {
        title: productMerchandisingData.category || '',
        imgUrl: product?.item?.image_url || '',
        price: price?.price || 0,
        size: productMerchandisingData.size,
    };

    // format arriving details
    const arrivingDetails = useMemo(() => {
        return registerInfo.deliveryDate || registerInfo.deliveryDate != 'Invalid Date'
            ? {
                date: formatDateWithDayOfWeek(registerInfo.deliveryDate),
                time: deliveryConfig?.delivery_time || '',
            }
            : undefined;
    }, [registerInfo, deliveryConfig]);

    const calcPriceData = useMemo<ICalcPriceData>(() => {
        if (product && price && !couponLoading) {
            return calculatePrice({
                coupon,
                couponError,
                couponId,
                shippingPrice: deliveryConfig?.delivery_costs,
                items: [{ price, product, quantity: registerInfo.quantity }],
            });
        }
        return {
            discountTitle: '',
            discountInfo: '',
            subTotalPrice: 0,
            totalPrice: 0,
            shippingPrice: 0,
        };
    }, [
        product,
        price,
        couponLoading,
        coupon,
        couponError,
        deliveryConfig?.delivery_costs,
        couponId,
        registerInfo.quantity,
    ]);

    const { timer } = useTimer(calcPriceData.disableCouponFieldFor);
    const shouldDisableCoupon = !!timer;

    const handleSubmit = (): void => {
        const couponIdForm = form.formContext.getValues().couponId;
        if (couponId !== couponIdForm) {
            saveToRegister({ couponId: couponIdForm });
            setCouponId(couponIdForm);
            couponMutate(couponIdForm).then((data) => {
                if (data) {
                    orderInfoMutate?.({
                        body: {
                            plan_item_price_id: registerInfo.priceId,
                            delivery_configuration_id: registerInfo.deliveryConfigId,
                            coupon_id: data.id,
                        },
                    });
                }
            });
        }
    };
    const form = useForm<IFormProps>({
        enableReinitialize: true,
        defaultValues: {
            couponId,
        },
        onSubmit: handleSubmit,
    });

    const couponErrorMessage = useMemo(() => {
        const error = orderInfoDataError?.error ? ERROR_CODES_MAP[orderInfoDataError?.error] : null;
        const maxAttemptsExceededError =
            timer &&
            `Applying coupons are temporarily blocked due to multiple invalid attempts. Please try again in ${timer} seconds`;
        return maxAttemptsExceededError || error;
    }, [orderInfoDataError?.error, timer]);

    const discounts = useMemo(
        () =>
            couponId && coupon
                ? [
                    coupon.discount_type === ECouponType.fixed_amount
                        ? {
                            amount: coupon.discount_amount || 0,
                            title: coupon.id || '',
                        }
                        : {
                            amount: coupon.discount_amount || 0,
                            title: coupon.id || '',
                            percentage_discount: coupon.label || '',
                        },
                ]
                : undefined,
        [couponId, coupon],
    );

    const handleSave = useCallback(
        (data: Partial<IRegisterModel>) => {
            saveToRegister(data);
        },
        [saveToRegister],
    );

    const handleClear = useCallback(() => {
        form.formContext.setValue('couponId', '');
        handleSave({
            couponId: undefined,
        });
        setCouponId(undefined);
    }, [form.formContext, handleSave]);

    useEffect(() => {
        customNotify.error([
            { beError: productError, onShown: productsRemove },
            { beError: deliveryConfigError, onShown: deliveryConfigsRemove },
        ]);
        if (couponError) {
            saveToRegister({
                couponId: undefined,
            });
        }
    }, [couponError, deliveryConfigError, deliveryConfigsRemove, productError, productsRemove, saveToRegister]);

    return (
        <div css={styles.root}>
            <Loading isLoading={productLoading || deliveryConfigLoading}>
                <Summary
                    title="Order Summary"
                    boxInfo={boxInfo}
                    address={registerInfo.shippingAddress.label}
                    arriving={arrivingDetails}
                    frequency={price?.clientDescription}
                    total={orderInfoData?.final_amount || 0}
                    addons={[]}
                    deliveryFee={{ price: orderInfoData?.delivery_fee?.original_amount || 0 }}
                    discounts={[
                        {
                            amount: orderInfoData?.discount_amount || 0,
                            title: (orderInfoData?.discounts?.length && orderInfoData?.discounts[0].description) || '',
                        },
                    ]}
                    discountAmount={orderInfoData?.discount_amount}
                    showTip={true}
                >
                    <Loading css={styles.loading} isLoading={couponLoading}>
                        {(!discounts || couponErrorMessage) && (
                            <Form form={form}>
                                <div
                                    tw="flex"
                                    data-testid="order-coupon-container"
                                    data-coupon-error={calcPriceData.error?.toString()}
                                >
                                    <FormField<IFormProps>
                                        className="couponInput"
                                        name="couponId"
                                        tw="border-[#D9D9D9] mr-3"
                                        readOnly={shouldDisableCoupon || !!couponId}
                                        {...FIELDS[EField.registerDiscountV2]}
                                    />

                                    {couponId ? (
                                        <ButtonV2
                                            onClick={handleClear}
                                            name="clearCoupon"
                                            variant="primary"
                                            disabled={
                                                !form.formContext.getValues().couponId ||
                                                couponLoading ||
                                                shouldDisableCoupon
                                            }
                                            tw="font-normal w-1/4 ml-1"
                                        >
                                            Clear
                                        </ButtonV2>
                                    ) : (
                                        <ButtonV2
                                            type="submit"
                                            name="applyDiscount"
                                            variant="primary"
                                            disabled={
                                                !form.formContext.getValues().couponId ||
                                                couponLoading ||
                                                shouldDisableCoupon
                                            }
                                            tw="font-normal w-1/4"
                                        >
                                            Apply
                                        </ButtonV2>
                                    )}
                                </div>
                            </Form>
                        )}

                        {couponErrorMessage && (
                            <StatusTagV2
                                tw="w-full mt-4"
                                name="Applied discount"
                                status="error"
                                title={couponErrorMessage}
                                matchTextColor={true}
                            >
                                {couponErrorMessage === 'Coupon not applicable'
                                    ? 'This coupon does not apply to your selected item/s'
                                    : ''}
                            </StatusTagV2>
                        )}

                        {couponId && orderInfoData?.discounts && !couponError && (
                            <StatusTagV2
                                tw="w-full"
                                name="Applied discount"
                                status="success-light"
                                title={`${orderInfoData?.discounts[0].description?.includes('Referral-coupon')
                                    ? 'Refer a friend'
                                    : `${orderInfoData?.discounts[0].description} code applied`
                                    }`}
                                customIcon={<Icon icon={PercentageOutlined} />}
                                showClose={true}
                                onCloseIconClick={handleClear}
                                matchTextColor={true}
                            />
                        )}
                    </Loading>
                </Summary>
            </Loading>
        </div>
    );
};

const styles = {
    root: css`
        .couponInput input {
            height: 3rem;
        }
    `,
    loading: css`
        display: flex;
        flex-direction: column;
        gap: 8px;
    `,
};
